// The delivery results of a launched campaign: the KPI header, the filters, and the per-recipient
// table with its status, failure reason and reply.
//
// Extracted VERBATIM from what used to be the whole CampaignResults page, so that merging that page
// with the draft editor could not quietly change any of it. Every effect, filter and query
// parameter below is the same code that was already working; only the surrounding shell moved.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import {
  Search, Loader2, ArrowLeft, RefreshCw, Download, Inbox, MessageSquareOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch, authFetch } from '@/lib/api';
import { usePermissions } from '@/contexts/WorkspaceContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import {
  MessageStatus,
  DeliveryStats,
  messageStatusVariant,
  messageStatusClass,
  messageStatusLabel,
  failureReason,
  deliveryRate,
  readRate,
} from '@/lib/messageStatus';

interface Reply {
  id: string;
  body: string | null;
  receivedAt: string;
  messageType: string;
  isOptOut: boolean;
}

interface Recipient {
  id: string;
  status: MessageStatus;
  channel: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failureCode: string | null;
  errorMessage: string | null;
  providerMessageId: string | null;
  creditsCharged: number | null;
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    propertyInterest: string | null;
    isUnsubscribed: boolean;
  } | null;
  reply: Reply | null;
}

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  createdAt: string;
}

const EMPTY_STATS: DeliveryStats = {
  total: 0, pending: 0, queued: 0, sent: 0, delivered: 0, read: 0,
  failed: 0, capped: 0, replied: 0, optedOut: 0,
};

const formatDateTime = (value: string | null) => (value
  ? new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
  : '—');

interface CampaignResultsPanelProps {
  workspaceId: string;
  campaignId: string;
  campaignName?: string;
  /**
   * Rendered beside the KPI tiles rather than above them.
   *
   * Passed in as a node instead of the caller laying the two out itself, because `stats` is this
   * component's own state — the tiles cannot move out to sit next to something else without either
   * lifting the fetch or running it twice.
   */
  aside?: ReactNode;
}

export const CampaignResultsPanel = ({ workspaceId, campaignId, campaignName, aside }: CampaignResultsPanelProps) => {
  const { can } = usePermissions();
  const showError = useApiErrorToast();

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [stats, setStats] = useState<DeliveryStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [repliedFilter, setRepliedFilter] = useState('any');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const filtersActive = Boolean(searchTerm) || statusFilter !== 'all' || repliedFilter !== 'any';

  const buildParams = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (repliedFilter !== 'any') params.append('replied', repliedFilter === 'replied' ? 'true' : 'false');
    return params;
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.append('page', page.toString());
      params.append('limit', '25');

      const data = await apiFetch<{
        recipients: Recipient[];
        stats: DeliveryStats;
        pagination: { totalPages: number };
      }>(`/workspaces/${workspaceId}/campaigns/${campaignId}/recipients?${params.toString()}`);

      setRecipients(data.recipients || []);
      setStats(data.stats || EMPTY_STATS);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error) {
      showError(error, 'Failed to load campaign results.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspaceId || !campaignId) return;
    fetchRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, campaignId, page, statusFilter, repliedFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchRecipients();
    }, 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  /**
   * Ask the provider about anything we have no verdict on yet, then reload.
   *
   * Deliberately a button and not a poll on a timer. WhatsApp statuses only ever arrive by webhook,
   * and the SMS side reconciles by calling the provider once per unresolved message — putting that
   * on an interval would hammer it for every operator who leaves this tab open.
   */
  const refreshDelivery = async () => {
    setRefreshing(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaigns/${campaignId}/poll-delivery`, {
        method: 'POST',
      });
      await fetchRecipients();
      toast.success('Delivery status refreshed');
    } catch (error) {
      showError(error, 'Could not refresh delivery status.');
    } finally {
      setRefreshing(false);
    }
  };

  const downloadCsv = () => {
    const params = buildParams();
    // The export needs the bearer token, so it cannot be a plain link. Fetched as a blob and handed
    // to the browser instead.
    authFetch(`/workspaces/${workspaceId}/campaigns/${campaignId}/recipients/export?${params}`)
      .then((response) => {
        if (!response.ok) throw new Error('export failed');
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${campaignName || 'campaign'}-recipients.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      // A raw fetch rather than apiFetch (it needs the blob, not the JSON envelope), so this is a
      // plain Error rather than an ApiError — the network branch still applies.
      .catch((error) => showError(error, 'Could not download the CSV.'));
  };

  const kpis = [
    { label: 'Recipients', value: stats.total, caption: '' },
    { label: 'Sent', value: stats.sent + stats.delivered + stats.read, caption: '' },
    {
      label: 'Delivered',
      value: stats.delivered + stats.read,
      caption: `${deliveryRate(stats)}% of attempted`,
    },
    { label: 'Read', value: stats.read, caption: `${readRate(stats)}% of delivered` },
    {
      label: 'Failed',
      value: stats.failed,
      // A subset of Failed, not a sibling — as its own tile it would read as extra failures on top.
      caption: stats.capped ? `${stats.capped} hit the daily cap` : '',
    },
    { label: 'Replied', value: stats.replied, caption: '' },
    { label: 'Opted out', value: stats.optedOut, caption: '' },
  ];

  return (
    <>
        {/* The message and how it did, read together. Seven tiles in a row leaves them thin and
            the preview stranded above; splitting the width gives each half a shape that fits it —
            a narrow column for a phone-sized bubble, a denser block for the numbers. */}
        <div className={cn('mb-6 grid gap-4', aside && 'lg:grid-cols-5')}>
          {aside && <div className="lg:col-span-2">{aside}</div>}

          <div
            className={cn(
              'grid grid-cols-2 gap-3 content-start',
              aside ? 'lg:col-span-3 xl:grid-cols-4' : 'md:grid-cols-4 lg:grid-cols-7',
            )}
          >
            {kpis.map((kpi) => (
              <Card key={kpi.label}>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-2xl font-bold">{kpi.value}</p>
                  {kpi.caption && (
                    <p className="text-xs text-muted-foreground mt-1">{kpi.caption}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recipients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, phone, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(value) => { setPage(1); setStatusFilter(value); }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="capped">Daily cap</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={repliedFilter}
                onValueChange={(value) => { setPage(1); setRepliedFilter(value); }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Replies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any reply</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="none">No reply</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : recipients.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {filtersActive || stats.total > 0 ? (
                  <>
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No recipients match these filters</p>
                    <p className="text-sm">Try adjusting the search or status filter.</p>
                  </>
                ) : (
                  <>
                    <Inbox className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">This campaign hasn't been launched yet</p>
                    <p className="text-sm">Results appear here once messages have been sent.</p>
                    {can('campaign:launch') && (
                      <Button className="mt-4" asChild>
                        <Link to={`/w/${workspaceId}/campaigns`}>Go to campaigns</Link>
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Sent</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Reply</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {recipients.map((recipient) => {
                          const reason = recipient.status === 'failed'
                            ? failureReason(recipient.failureCode, recipient.errorMessage)
                            : null;

                          return (
                            <TableRow key={recipient.id}>
                              <TableCell>
                                <div className="font-medium">
                                  {recipient.contact?.name || 'Unnamed'}
                                </div>
                                <div className="text-xs text-muted-foreground" dir="ltr">
                                  {recipient.contact?.phone || '—'}
                                </div>
                              </TableCell>

                              <TableCell>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant={messageStatusVariant(recipient.status, recipient.failureCode)}
                                      className={messageStatusClass(recipient.status, recipient.failureCode)}
                                    >
                                      {messageStatusLabel(recipient.status, recipient.failureCode)}
                                    </Badge>
                                  </TooltipTrigger>
                                  {/* All three timestamps live here rather than as three more
                                      columns, which would be unreadable at this width. */}
                                  <TooltipContent>
                                    <div className="text-xs space-y-1">
                                      <div>Sent: {formatDateTime(recipient.sentAt)}</div>
                                      <div>Delivered: {formatDateTime(recipient.deliveredAt)}</div>
                                      <div>Read: {formatDateTime(recipient.readAt)}</div>
                                      {recipient.channel && <div>Channel: {recipient.channel}</div>}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TableCell>

                              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                {formatDateTime(recipient.sentAt)}
                              </TableCell>

                              <TableCell className="max-w-[220px]">
                                {reason ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-sm underline decoration-dotted cursor-help">
                                        {reason.label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="text-xs">{reason.hint}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>

                              <TableCell className="max-w-[280px]">
                                {recipient.reply ? (
                                  <div className="space-y-1">
                                    {/* dir="auto" because most replies in this product are Arabic,
                                        and the app is in an LTR layout unless the UI language is
                                        switched. Without it a mixed-script reply renders mangled. */}
                                    <p className="text-sm line-clamp-2" dir="auto">
                                      {recipient.reply.body || `(${recipient.reply.messageType})`}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground">
                                        {formatDateTime(recipient.reply.receivedAt)}
                                      </span>
                                      {recipient.reply.isOptOut && (
                                        <Badge variant="destructive" className="text-xs">
                                          <MessageSquareOff className="h-3 w-3 mr-1" />
                                          Opted out
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                {totalPages > 1 && (
                  <div className="mt-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => setPage(pageNum)}
                                isActive={page === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
    </>
  );
};

export default CampaignResultsPanel;
